import React, { useCallback } from 'react';
import styled from '@emotion/styled';
import { FormControl, Grid, IconButton, Input } from '@mui/material';
import { Add, Remove } from '@mui/icons-material';
import {
  COLOR_CARNATION,
  COLOR_DENIM,
  COLOR_LIGHT_GREY,
  COLOR_WHITE,
  FONT_SIZE_16_PX,
  FONT_WEIGHT_SEMI_BOLD,
} from 'popTheme';
import PropTypes from 'prop-types';

const StyledIconButton = styled(IconButton)`
  && {
    display: flex;
    justify-content: center;
    align-items: center;
    box-sizing: border-box;
    width: 1.5rem;
    height: 1.5rem;
    border-radius: 50%;
    color: ${(props) => (props.disabled ? COLOR_WHITE : COLOR_CARNATION)};
    border: 2px solid ${(props) => (props.disabled ? COLOR_LIGHT_GREY : COLOR_CARNATION)};
    background: ${(props) => (props.disabled ? COLOR_LIGHT_GREY : 'transparent')};
    cursor: pointer;
    &:hover,
    &:focus,
    &:focus-within {
      background: ${(props) => (props.disabled ? COLOR_LIGHT_GREY : COLOR_CARNATION)};
      color: ${COLOR_WHITE};
    }
  }
`;

export const CounterInput = ({ value, min = 0, max = 14, step = 1, onChange, unitLabel }) => {
  const clampValue = useCallback(
    (v) => {
      if (v < min) return min;
      if (v > max) return max;
      return v;
    },
    [min, max],
  );

  const INVALID = min - 1; // keeps the math easy for the first user interaction
  const modelValue = Number.isFinite(value) ? clampValue(value) : INVALID;
  const displayedValue = modelValue === INVALID ? '-' : modelValue;

  const handleDecrement = () => onChange(clampValue(modelValue - step));
  const handleIncrement = () => onChange(clampValue(modelValue + step));
  const handleInputChange = (event) => {
    event.preventDefault();
    const inputValue = parseInt(event.target.value, 10);
    inputValue >= min && inputValue <= max && onChange(inputValue);
  };

  return (
    <FormControl>
      <Grid
        container
        alignItems="center"
        justifyContent="center"
        sx={{
          py: '0.125rem',
          gap: '1.5rem',
        }}
      >
        <Grid item>
          <StyledIconButton
            aria-label="decrement"
            disabled={value === min}
            onClick={handleDecrement}
            data-testid="decrement-button"
          >
            <Remove />
          </StyledIconButton>
        </Grid>
        <Grid
          item
          sx={{
            borderBottom: `3px solid ${COLOR_CARNATION}`,
            pb: '0.5rem',
            paddingInline: '2.5rem',
            fontWeight: FONT_WEIGHT_SEMI_BOLD,
            fontSize: FONT_SIZE_16_PX,
          }}
        >
          <Input
            disableUnderline
            id="standard-number"
            variant="standard"
            value={displayedValue}
            endAdornment={unitLabel}
            onChange={handleInputChange}
            inputProps={{
              min: min,
              max: max,
            }}
            sx={{
              width: '4.125rem',
              color: COLOR_DENIM,
            }}
          />
        </Grid>
        <Grid item>
          <StyledIconButton
            aria-label="increment"
            disabled={value === max}
            onClick={handleIncrement}
            data-testid="increment-button"
          >
            <Add />
          </StyledIconButton>
        </Grid>
      </Grid>
    </FormControl>
  );
};

CounterInput.propTypes = {
  min: PropTypes.number,
  max: PropTypes.number,
  step: PropTypes.number,
  onChange: PropTypes.func.isRequired,
  unitLabel: PropTypes.string.isRequired,
};
